import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { MerchCartDrawer } from "@/components/merch/MerchCartDrawer";
import { MerchCartFloatingButton } from "@/components/merch/MerchCartFloatingButton";

export interface MerchCartItem {
  /** Unique key: productId|size|color|gender|storeOwnerId */
  key: string;
  productId: string;
  name: string;
  shortName: string;
  image: string;
  size?: string;
  color?: string;
  gender?: string;
  /** Unit price in DKK (whole kroner) */
  priceDKK: number;
  quantity: number;
  storeOwnerId?: string;
  isOfficialStore?: boolean;
}

interface MerchCartContextValue {
  items: MerchCartItem[];
  count: number;
  subtotalDKK: number;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  addItem: (item: Omit<MerchCartItem, "key" | "quantity">, quantity?: number) => void;
  removeItem: (key: string) => void;
  setQuantity: (key: string, quantity: number) => void;
  clear: () => void;
}

const MerchCartContext = createContext<MerchCartContextValue | null>(null);

const STORAGE_KEY = "merch-cart-v1";
const MAX_QTY = 5;

function loadCart(): MerchCartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((i) => i && typeof i.key === "string" && typeof i.productId === "string");
  } catch {
    return [];
  }
}

export function MerchCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<MerchCartItem[]>(loadCart);
  const [isOpen, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // storage full/blocked — cart still works in memory
    }
  }, [items]);

  const value = useMemo<MerchCartContextValue>(() => {
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotalDKK = items.reduce((sum, i) => sum + i.priceDKK * i.quantity, 0);
    return {
      items,
      count,
      subtotalDKK,
      isOpen,
      setOpen,
      addItem: (item, quantity = 1) => {
        const key = [item.productId, item.size ?? "", item.color ?? "", item.gender ?? "", item.storeOwnerId ?? ""].join("|");
        setItems((prev) => {
          const existing = prev.find((i) => i.key === key);
          if (existing) {
            return prev.map((i) =>
              i.key === key ? { ...i, quantity: Math.min(MAX_QTY, i.quantity + quantity) } : i,
            );
          }
          return [...prev, { ...item, key, quantity: Math.min(MAX_QTY, quantity) }];
        });
      },
      removeItem: (key) => setItems((prev) => prev.filter((i) => i.key !== key)),
      setQuantity: (key, quantity) =>
        setItems((prev) =>
          quantity <= 0
            ? prev.filter((i) => i.key !== key)
            : prev.map((i) => (i.key === key ? { ...i, quantity: Math.min(MAX_QTY, quantity) } : i)),
        ),
      clear: () => setItems([]),
    };
  }, [items, isOpen]);

  const showFloatingButton =
    value.count > 0 && (location.pathname.startsWith("/merch") || location.pathname.startsWith("/halloween"));

  return (
    <MerchCartContext.Provider value={value}>
      {children}
      <MerchCartDrawer />
      {showFloatingButton && <MerchCartFloatingButton />}
    </MerchCartContext.Provider>
  );
}

export function useMerchCart(): MerchCartContextValue {
  const ctx = useContext(MerchCartContext);
  if (!ctx) throw new Error("useMerchCart must be used within MerchCartProvider");
  return ctx;
}
